import type {
  ChallengeKind,
  IncomingChallenge,
  Opponent,
  OutgoingChallenge,
} from '@sea-wars/protocol';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from './client.js';
import { useSession } from './session.js';

/** Odpytywanie o wyzwania — lżejsze niż kolejka rankingowa (2 s), bo mniej pilne. */
const CHALLENGES_POLL_MS = 4000;

/** Wynik zgłoszenia rewanżu — `busy` znaczy, że przeciwnik jest teraz w trakcie innej partii. */
export type RequestRematchResult = 'offered' | 'ready' | 'busy';

/** Kogo wyzywamy — ostatni przeciwnik z lobby albo dowolny gracz z listy graczy. */
export type ChallengeTarget = Pick<Opponent, 'userId' | 'nickname'>;

export interface BusyNotice {
  readonly kind: ChallengeKind;
  readonly nickname: string;
}

interface ChallengesValue {
  readonly incoming: IncomingChallenge[];
  readonly outgoing: OutgoingChallenge[];
  /**
   * Wyzwanie/oferta rewanżu do pokazania w popupie — pierwsza nieodłożona „na później”
   * pozycja z `incoming` (serwer zwraca je najświeższe-najpierw).
   */
  readonly activeIncoming: IncomingChallenge | null;
  readonly snoozedCount: number;
  readonly busyNotice: BusyNotice | null;
  readonly challenge: (opponent: ChallengeTarget) => Promise<void>;
  readonly respond: (challengeId: string, accept: boolean) => Promise<void>;
  readonly cancel: (challengeId: string) => Promise<void>;
  readonly requestRematch: (
    gameId: string,
    opponentNickname: string,
  ) => Promise<RequestRematchResult>;
  readonly snooze: (challengeId: string) => void;
  readonly unsnoozeAll: () => void;
  readonly dismissBusyNotice: () => void;
}

const ChallengesContext = createContext<ChallengesValue | null>(null);

/**
 * Wyzwania NA POJEDYNEK i oferty rewanżu na poziomie całej aplikacji, nie tylko lobby czy
 * ekranu partii (docs/08-decyzje.md #21) — odpytywane REST-em, bo nie ma infrastruktury do
 * wypchnięcia zdarzenia do konkretnego użytkownika poza kontekstem już istniejącego pokoju
 * gry. Rewanż dawniej żył wyłącznie w pamięci `GameRoom` (widoczny tylko z otwartym
 * połączeniem WS do TEJ partii) — teraz jest wyzwaniem specjalnego rodzaju (`kind: 'rematch'`,
 * `sourceGameId`), więc dostaje dokładnie tę samą globalną widoczność co zwykłe wyzwanie.
 */
export function ChallengesProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState<IncomingChallenge[]>([]);
  const [outgoing, setOutgoing] = useState<OutgoingChallenge[]>([]);
  const [snoozed, setSnoozed] = useState<Set<string>>(new Set());
  const [busyNotice, setBusyNotice] = useState<BusyNotice | null>(null);
  const handledAcceptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) {
      setIncoming([]);
      setOutgoing([]);
      return;
    }
    let cancelled = false;
    async function poll() {
      try {
        const res = await api.challenges();
        if (cancelled) return;
        setIncoming(res.incoming);
        setOutgoing(res.outgoing);
        // Zaakceptowane (przeze mnie wysłane, albo obustronna oferta rewanżu, która się
        // właśnie domknęła) — wchodzimy do nowej partii automatycznie, bez klikania niczego.
        const accepted = res.outgoing.find(
          (item) =>
            item.status === 'accepted' && item.gameId && !handledAcceptedRef.current.has(item.id),
        );
        if (accepted?.gameId) {
          handledAcceptedRef.current.add(accepted.id);
          navigate(`/game/${accepted.gameId}`);
        }
      } catch {
        // Pojedynczy nieudany odczyt pomijamy cicho — kolejny poll spróbuje ponownie.
      }
    }
    void poll();
    const timer = window.setInterval(() => void poll(), CHALLENGES_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [user, navigate]);

  const challenge = useCallback(async (opponent: ChallengeTarget) => {
    try {
      const result = await api.createChallenge(opponent.userId);
      setOutgoing((prev) =>
        prev.some((item) => item.id === result.id)
          ? prev
          : [
              ...prev,
              {
                id: result.id,
                kind: 'challenge',
                sourceGameId: null,
                to: { userId: opponent.userId, nickname: opponent.nickname },
                status: 'pending',
                gameId: null,
                createdAt: new Date().toISOString(),
              },
            ],
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'TARGET_BUSY') {
        setBusyNotice({ kind: 'challenge', nickname: opponent.nickname });
        return;
      }
      throw error;
    }
  }, []);

  const respond = useCallback(
    async (challengeId: string, accept: boolean) => {
      if (accept) {
        const result = await api.acceptChallenge(challengeId);
        navigate(`/game/${result.gameId}`);
        return;
      }
      await api.declineChallenge(challengeId);
      setIncoming((prev) => prev.filter((item) => item.id !== challengeId));
    },
    [navigate],
  );

  const cancel = useCallback(async (challengeId: string) => {
    await api.cancelChallenge(challengeId);
    setOutgoing((prev) => prev.filter((item) => item.id !== challengeId));
  }, []);

  /**
   * REST-owy odpowiednik dawnej komendy WS `game.rematch` — patrz `createRematchOffer` na
   * serwerze. `'ready'` (przeciwnik już wcześniej wysłał tę samą ofertę, albo to bot) nawiguje
   * od razu, tak jak przy przyjętym wyzwaniu; `'offered'` zostawia decyzję wywołującemu
   * (`GamePage` pokazuje wtedy „Czekasz na przeciwnika…”).
   */
  const requestRematch = useCallback(
    async (gameId: string, opponentNickname: string): Promise<RequestRematchResult> => {
      try {
        const result = await api.requestRematch(gameId);
        if (result.status === 'ready') {
          navigate(`/game/${result.gameId}`);
          return 'ready';
        }
        return 'offered';
      } catch (error) {
        if (error instanceof ApiError && error.code === 'TARGET_BUSY') {
          setBusyNotice({ kind: 'rematch', nickname: opponentNickname });
          return 'busy';
        }
        throw error;
      }
    },
    [navigate],
  );

  const snooze = useCallback((challengeId: string) => {
    setSnoozed((prev) => new Set(prev).add(challengeId));
  }, []);

  const unsnoozeAll = useCallback(() => setSnoozed(new Set()), []);

  const dismissBusyNotice = useCallback(() => setBusyNotice(null), []);

  const activeIncoming = useMemo(
    () => incoming.find((item) => !snoozed.has(item.id)) ?? null,
    [incoming, snoozed],
  );
  // Przecięte z `incoming`, nie samo `snoozed.size` — inaczej znacznik w nagłówku nie
  // zniknąłby po tym, jak odłożona pozycja zniknęła z listy z innego powodu (np. wyzywający
  // ją anulował, albo lustrzana oferta rewanżu domknęła się sama).
  const snoozedCount = useMemo(
    () => incoming.filter((item) => snoozed.has(item.id)).length,
    [incoming, snoozed],
  );

  return (
    <ChallengesContext.Provider
      value={{
        incoming,
        outgoing,
        activeIncoming,
        snoozedCount,
        busyNotice,
        challenge,
        respond,
        cancel,
        requestRematch,
        snooze,
        unsnoozeAll,
        dismissBusyNotice,
      }}
    >
      {children}
    </ChallengesContext.Provider>
  );
}

export function useChallenges(): ChallengesValue {
  const value = useContext(ChallengesContext);
  if (!value) throw new Error('useChallenges poza ChallengesProvider');
  return value;
}
